/**
 * SectionEditorModal — focused modal editor for a single content section
 * (DEV-1265 / Skip 2026-06-08). Replaces the old toggle-page → scroll →
 * inline-edit flow on the About tab with a one-click modal, for BOTH
 * mini-projects and full projects (the shared editing surface — minis are
 * the training ground that mirrors full-project features).
 *
 * Two targeting modes:
 *   • sectionGuid     — edit a specific existing section.
 *   • findOrCreateType — edit the section of this type, or create it on
 *                        save if absent (e.g. 'retrospective').
 *
 * Reuses the shared SectionBodyEditor (TipTap + inline photos via Figure),
 * so editing is identical to the inline editor it replaces — just in a
 * readable, focused overlay. Opaque per the CLAUDE.md "Overlays MUST be
 * opaque" rule.
 */

import { useCallback, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { useApiProjectSections } from "../../api/useApiProjectSections";
import type { ContentSectionEntityType } from "../../api/projectContentSections";
import SectionBodyEditor from "../ProjectContentSections/SectionBodyEditor";
import styles from "./SectionEditorModal.module.css";

interface Props {
  entityType: ContentSectionEntityType;
  entityGuid: string;
  /** Edit a specific existing section. */
  sectionGuid?: string;
  /** OR find-or-create a section of this type (e.g. 'retrospective'). */
  findOrCreateType?: string;
  /** Title applied when creating (find-or-create, or a new custom section). */
  defaultTitle?: string;
  /** Allow renaming the section (custom sections only). Default false. */
  allowTitleEdit?: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

export default function SectionEditorModal({
  entityType,
  entityGuid,
  sectionGuid,
  findOrCreateType,
  defaultTitle,
  allowTitleEdit = false,
  onClose,
  onSaved,
}: Props) {
  const { t } = useAppTranslation("pages");
  const { sections, loading, addSection, editSection } = useApiProjectSections(
    entityType,
    entityGuid,
  );

  const existing = useMemo(() => {
    if (sectionGuid) return sections.find((s) => s.sectionGuid === sectionGuid) ?? null;
    if (findOrCreateType)
      return sections.find((s) => s.sectionType === findOrCreateType) ?? null;
    return null;
  }, [sections, sectionGuid, findOrCreateType]);

  // Default title for find-or-create when the caller didn't pass one
  // (e.g. the retrospective flow → "Retrospective").
  const resolvedDefaultTitle = useMemo(
    () =>
      defaultTitle ||
      (findOrCreateType === "retrospective"
        ? t("projectDetail.about.retrospectiveTitle")
        : ""),
    [defaultTitle, findOrCreateType, t],
  );

  const [title, setTitle] = useState<string>(existing?.title || resolvedDefaultTitle || "");
  const jsonRef = useRef<string>(existing?.bodyJson || "");
  const plainRef = useRef<string>(existing?.bodyPlain || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Title is only initialized from `existing` once it resolves (async
  // load). Sync it when the section arrives so edit-mode shows the real
  // title. Guarded so user keystrokes aren't clobbered.
  const titleInitRef = useRef(false);
  if (!titleInitRef.current && !loading && existing) {
    titleInitRef.current = true;
    if (existing.title && existing.title !== title) setTitle(existing.title);
  }

  const handleUpdate = useCallback((json: string, plain: string) => {
    jsonRef.current = json;
    plainRef.current = plain;
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      if (existing) {
        await editSection(existing.sectionGuid, {
          ...(allowTitleEdit && title.trim() ? { title: title.trim() } : {}),
          bodyJson: jsonRef.current,
          bodyPlain: plainRef.current,
        });
      } else {
        const nextOrder =
          sections.length > 0 ? Math.max(...sections.map((s) => s.sortOrder)) + 1 : 0;
        await addSection({
          title: (title.trim() || resolvedDefaultTitle || "Section"),
          sectionType: findOrCreateType || "custom",
          bodyJson: jsonRef.current,
          bodyPlain: plainRef.current,
          sortOrder: nextOrder,
          isVisible: true,
        });
      }
      onSaved?.();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSaving(false);
    }
  }, [existing, editSection, addSection, allowTitleEdit, title, sections, findOrCreateType, resolvedDefaultTitle, onSaved, onClose]);

  const heading = existing
    ? existing.title
    : defaultTitle || t("projectDetail.about.customTitle");

  const dialog = (
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className={styles.head}>
          <div className={styles.headText}>
            {allowTitleEdit ? (
              <input
                className={styles.titleInput}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t("projectDetail.about.sectionTitlePlaceholder")}
                aria-label={t("projectDetail.about.sectionTitlePlaceholder")}
              />
            ) : (
              <h2 className={styles.title}>{heading}</h2>
            )}
            <p className={styles.subtitle}>{t("projectDetail.about.editorHint")}</p>
          </div>
          <button className={styles.x} onClick={onClose} aria-label={t("miniProject.completion.close")}>
            ✕
          </button>
        </div>

        <div className={styles.body}>
          {loading ? (
            <div className={styles.loading}>{t("miniProject.completion.loadingRetro")}</div>
          ) : (
            <SectionBodyEditor
              key={existing?.sectionGuid || "new"}
              initialContent={existing?.bodyJson || ""}
              placeholder={t("projectDetail.about.emptyBodyPlaceholder")}
              onUpdate={handleUpdate}
              t={t as (key: string) => string}
            />
          )}
          {error && <div className={styles.error}>{error}</div>}
        </div>

        <div className={styles.foot}>
          <span className={styles.spacer} />
          <button className={styles.btnGhost} onClick={onClose} disabled={saving}>
            {t("miniProject.completion.cancel")}
          </button>
          <button className={styles.btnPrimary} onClick={handleSave} disabled={saving}>
            {saving ? t("miniProject.completion.saving") : t("projectDetail.about.saveSection")}
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(dialog, document.body);
}
